/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\aiAgentsTypes.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/


export interface AiAgent {
    id: number;
    uuid: string;
    domainId: number;
    agentName: string;
    extension: string | null;
    description: string | null;
    aiProfileId: number | null;
    promptGeneratorProfileId: number | null;
    voice: string;
    conversationStart: string;
    conversationMode: string;
    languageMode: string;
    forcedLanguage: string | null;
    bargeInEnabled: boolean;
    bargeInSensitivity: number;
    bargeInMinSpeechMs: number;
    greetingProtectionMs: number;
    greetingProtection: boolean;
    idleTimeoutMs: number;
    idleRetries: number;
    audioDrainMs: number;
    transcriptionEnabled: boolean;
    transcriptionProfileId: number | null;
    storeTranscripts: boolean;
    liveTranscript: boolean;
    recordCall: boolean;
    translationEnabled: boolean;
    translationTargetLanguage: string | null;
    disclosureEnabled: boolean;
    disclosureMessage: string | null;
    piiRedactionEnabled: boolean;
    finalDestinationType: string | null;
    finalDestinationData: Record<string, unknown> | null;
    fallbackDestinationType: string | null;
    fallbackDestinationData: Record<string, unknown> | null;
    maxDurationSeconds: number;
    systemPrompt: string | null;
    greetingMessage: string | null;
    // Structured Persona
    personaName: string | null;
    roleDescription: string | null;
    personality: string | null;
    toneOfVoice: string | null;
    languageLevel: string | null;
    speakingStyle: string | null;
    responseLength: string | null;
    fillerWords: boolean;
    reduceRepetition: boolean;
    allowLanguageChange: boolean;
    companyName: string | null;
    products: string[] | null;
    keyPoints: string | null;
    teamMembers: Record<string, string> | null;
    doRules: string[] | null;
    dontRules: string[] | null;
    pronunciations: Record<string, string> | null;
    closingMessage: string | null;
    additionalInstructions: string | null;
    // AI Function Capabilities
    enableHangup: boolean;
    enableTransfer: boolean;
    openTransfer: boolean;
    transferRoutes: { name: string; destination_type: string; destination: string }[] | null;
    // Destination Routing
    timeoutDestinationType: string | null;
    timeoutDestination: string | null;
    failureDestinationType: string | null;
    failureDestination: string | null;
    promptOutdated: boolean;
    enabled: boolean;
    isDefaultChatbot: boolean;
    integrationIds: string[];
    createdAt: string | null;
    updatedAt: string | null;
}

export interface AiAgentCall {
    id: number;
    uuid: string;
    agentId: number;
    callUuid: string;
    callerIdNumber: string | null;
    callerIdName: string | null;
    startTime: string;
    endTime: string | null;
    durationSeconds: number | null;
    totalBargeIns: number;
    totalAiTurns: number;
    totalCallerTurns: number;
    disposition: string | null;
    status: string;
    tokensInputText: number;
    tokensInputAudio: number;
    tokensOutputText: number;
    tokensOutputAudio: number;
    tokensCachedText: number;
    tokensCachedAudio: number;
    tokensTranscription: number;
    tokensTotal: number;
}

export interface AiAgentTranscript {
    id: number;
    callId: number;
    speaker: string;
    content: string;
    timestamp: string;
    interrupted: boolean;
}

export interface GeneratePromptInput {
    promptGeneratorProfileId: number;
    personaName?: string;
    roleDescription?: string;
    personality?: string;
    toneOfVoice?: string;
    languageLevel?: string;
    speakingStyle?: string;
    responseLength?: string;
    fillerWords?: boolean;
    reduceRepetition?: boolean;
    allowLanguageChange?: boolean;
    language?: string;
    companyName?: string;
    products?: string[];
    doRules?: string[];
    dontRules?: string[];
    greetingMessage?: string;
    closingMessage?: string;
    keyPoints?: string;
    enableTransfer?: boolean;
    enableHangup?: boolean;
    disclosureMessage?: string;
    additionalInstructions?: string;
}

export interface DynamicContext {
    id: number;
    uuid: string;
    agentId: number;
    sourceDocumentId: number | null;
    sourceType: 'manual' | 'document' | 'url';
    sourceUrl: string | null;
    contextKey: string;
    contextName: string;
    contextContent: string;
    triggerKeywords: string[];
    triggerIntents: string[];
    priority: number;
    reusePolicy: 'once_per_session' | 'reusable';
    tokenCount: number | null;
    enabled: boolean;
    createdAt: string;
    updatedAt: string | null;
}

export interface DynamicContextInput {
    contextKey: string;
    contextName: string;
    contextContent: string;
    triggerKeywords?: string[];
    triggerIntents?: string[];
    priority?: number;
    reusePolicy?: 'once_per_session' | 'reusable';
    enabled?: boolean;
}

export interface ContextDocument {
    id: number;
    uuid: string;
    agentId: number;
    originalFilename: string;
    storedPath: string;
    fileType: string;
    fileSize: number | null;
    extractionPrompt: string | null;
    processedAt: string | null;
    processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
    processingError: string | null;
    contextsExtracted: number;
    createdAt: string;
}

// ── Shared helpers ─────────────────────────────────────────────
function snakeToCamel(str: string): string {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function transformToApiFormat(data: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            const camelKey = snakeToCamel(key);
            let val = data[key];
            
            // Convert specific numeric ID fields from string to number/null
            if (['aiProfileId', 'toolProfileId', 'promptGeneratorProfileId', 'embeddingProfileId', 'transcriptionProfileId', 'providerId'].includes(camelKey)) {
                if (val === '' || val === 'none' || val === 'null' || val === 'undefined' || val === null || val === undefined) {
                    val = null;
                } else {
                    const parsed = Number(val);
                    if (!isNaN(parsed)) {
                        val = parsed;
                    }
                }
            }
            
            result[camelKey] = val;
        }
    }
    return result;
}

